import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InfoModal } from '@/components/feedback/InfoModal';
import { toast } from '@/components/feedback/useToast';

import { PASSWORD_MAX, PASSWORD_MIN, isValidPassword } from '@/shared/utils/password';

import { MyProfileApiError } from '../api/my-profile.types';
import { useChangePasswordMutation } from '../hooks/use-my-profile-mutations';

interface PasswordChangeFormProps {
  // Fired after a successful change so the parent can collapse the edit area
  // (PRD §3.4 step 5 "edit area automatically collapses").
  onSaved: () => void;
}

interface FieldErrors {
  current?: string;
  next?: string;
  confirm?: string;
}

export function PasswordChangeForm({ onSaved }: PasswordChangeFormProps) {
  const { t } = useTranslation('my-profile');
  const changePassword = useChangePasswordMutation();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [failureOpen, setFailureOpen] = useState(false);
  const currentRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    currentRef.current?.focus();
  }, []);

  const validate = (): FieldErrors => {
    const next_errors: FieldErrors = {};
    if (!current) {
      next_errors.current = t('passwordChange.errors.currentRequired');
    } else if (current.length < PASSWORD_MIN || current.length > PASSWORD_MAX) {
      // A current password outside the 8–64 length bound can't be the stored one —
      // block the BE round-trip (SHCT-321). Correctness stays server-verified (PRD §3.4).
      next_errors.current = t('passwordChange.errors.currentLength');
    }
    if (!next) {
      next_errors.next = t('passwordChange.errors.newRequired');
    } else if (!isValidPassword(next)) {
      next_errors.next = t('passwordChange.errors.newPolicy');
    } else if (current && next === current) {
      next_errors.next = t('passwordChange.errors.newSameAsCurrent');
    }
    if (!confirm) {
      next_errors.confirm = t('passwordChange.errors.confirmRequired');
    } else if (next !== confirm) {
      next_errors.confirm = t('passwordChange.errors.confirmMismatch');
    }
    return next_errors;
  };

  const handleSubmit = () => {
    if (changePassword.isPending) return;
    const v = validate();
    if (Object.keys(v).length > 0) {
      setErrors(v);
      return;
    }
    setErrors({});
    changePassword.mutate(
      { currentPassword: current, newPassword: next, confirmPassword: confirm },
      {
        onSuccess: () => {
          // 204 — no body. The mutation invalidates /my-profile; the refetch updates
          // the auth store's passwordChangedAt, refreshing card 2's header.
          toast.success(t('passwordChange.toasts.success'));
          onSaved();
        },
        onError: (e) => {
          if (e instanceof MyProfileApiError) {
            // Validation-class errors → inline (PRD §8.1). Operational failures
            // (network, INTERNAL_ERROR, anything unmapped) → popup (PRD §8.2).
            // Wire codes from the backend ErrorCode catalog.
            if (e.code === 'PASSWORD_INVALID') {
              setErrors({ current: t('passwordChange.errors.currentMismatch') });
              return;
            }
            if (e.code === 'PASSWORD_POLICY_VIOLATION') {
              setErrors({ next: t('passwordChange.errors.newPolicy') });
              return;
            }
            // SHCT-587 — PASSWORD_REUSED covers "same as current" OR "one of the last 3"
            // server-side, but `validate()` already blocks the same-as-current case before
            // submit, so a code arriving here is always a history reuse. It previously
            // showed the current-password copy, which misled the user.
            if (e.code === 'PASSWORD_REUSED') {
              setErrors({ next: t('passwordChange.errors.newReused') });
              return;
            }
            if (e.code === 'PASSWORD_CONFIRM_MISMATCH') {
              setErrors({ confirm: t('passwordChange.errors.confirmMismatch') });
              return;
            }
          }
          setFailureOpen(true);
        },
      },
    );
  };

  // Disable submit until every field has at least one character (PRD §3.4
  // "All fields empty → disable").
  const submitDisabled =
    !current || !next || !confirm || changePassword.isPending;

  const handleFieldChange = (field: keyof FieldErrors, value: string) => {
    if (field === 'current') setCurrent(value);
    else if (field === 'next') setNext(value);
    else setConfirm(value);
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  return (
    <>
      <div
        style={{
          marginTop: 12,
          padding: 16,
          background: '#fafbff',
          border: '1px solid #dbeafe',
          borderRadius: 6,
        }}
      >
        <div className="form-field" style={{ marginBottom: 16 }}>
          <label className="form-field__label" htmlFor="mpPwdCurrent">
            {t('passwordChange.fields.current.label')}
          </label>
          <input
            id="mpPwdCurrent"
            ref={currentRef}
            type="password"
            autoComplete="current-password"
            maxLength={64}
            className={`form-field__input${errors.current ? ' form-field__input--error' : ''}`}
            value={current}
            onChange={(e) => handleFieldChange('current', e.target.value)}
            disabled={changePassword.isPending}
            aria-invalid={!!errors.current}
            aria-describedby={errors.current ? 'mpPwdCurrentErr' : undefined}
          />
          {errors.current && (
            <p id="mpPwdCurrentErr" className="form-field__error" role="alert">
              {errors.current}
            </p>
          )}
        </div>

        <div className="form-field" style={{ marginBottom: 16 }}>
          <label className="form-field__label" htmlFor="mpPwdNew">
            {t('passwordChange.fields.new.label')}
          </label>
          <input
            id="mpPwdNew"
            type="password"
            autoComplete="new-password"
            maxLength={64}
            placeholder={t('passwordChange.fields.new.placeholder')}
            className={`form-field__input${errors.next ? ' form-field__input--error' : ''}`}
            value={next}
            onChange={(e) => handleFieldChange('next', e.target.value)}
            disabled={changePassword.isPending}
            aria-invalid={!!errors.next}
            aria-describedby={errors.next ? 'mpPwdNewErr' : undefined}
          />
          {errors.next && (
            <p id="mpPwdNewErr" className="form-field__error" role="alert">
              {errors.next}
            </p>
          )}
        </div>

        <div className="form-field" style={{ marginBottom: 16 }}>
          <label className="form-field__label" htmlFor="mpPwdConfirm">
            {t('passwordChange.fields.confirm.label')}
          </label>
          <input
            id="mpPwdConfirm"
            type="password"
            autoComplete="new-password"
            maxLength={64}
            className={`form-field__input${errors.confirm ? ' form-field__input--error' : ''}`}
            value={confirm}
            onChange={(e) => handleFieldChange('confirm', e.target.value)}
            disabled={changePassword.isPending}
            aria-invalid={!!errors.confirm}
            aria-describedby={errors.confirm ? 'mpPwdConfirmErr' : undefined}
          />
          {errors.confirm && (
            <p id="mpPwdConfirmErr" className="form-field__error" role="alert">
              {errors.confirm}
            </p>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn btn-default btn-primary"
            style={{ height: 40 }}
            disabled={submitDisabled}
            onClick={handleSubmit}
          >
            {changePassword.isPending
              ? t('passwordChange.actions.submitting')
              : t('passwordChange.actions.submit')}
          </button>
        </div>
      </div>

      <InfoModal
        open={failureOpen}
        title={t('passwordChange.failure.title')}
        body={t('passwordChange.failure.message')}
        confirmLabel={t('passwordChange.failure.close')}
        onClose={() => setFailureOpen(false)}
      />
    </>
  );
}
